import React, { useEffect, useState, useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useTranslation } from 'react-i18next';

interface ExchangeCardProps {
  logo: string;
  buttonText: string;
  buttonLink: string;
}

const ExchangeCard: React.FC<ExchangeCardProps> = ({ logo, buttonText, buttonLink }) => {
  return (
    <div className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_50%] lg:flex-[0_0_36.67%] lg:max-w-[330px] pl-4">
      <div className="bg-[#0F1C33] rounded-lg p-8 h-full flex flex-col items-center transition-all duration-300 hover:shadow-[0_0_30px_rgba(18,198,232,0.15)]">
        <img 
          src={logo} 
          alt={`${buttonText.replace('Copy on ', '')} cryptocurrency exchange platform logo`}
          className="h-[2.977rem] object-contain"
        />
        <div className="flex-grow min-h-[40px]"></div>
        <a 
          href={buttonLink} 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn btn-accent w-full text-center flex items-center justify-center group"
        >
          {buttonText}
          <ExternalLink size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </div>
    </div>
  );
};

const CopyTrading: React.FC = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [autoplayInstance] = useState(() => 
    Autoplay({ 
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
      rootNode: (emblaRoot) => emblaRoot.parentElement,
    })
  );

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      dragFree: false,
      containScroll: 'trimSnaps',
      align: 'start',
      speed: 40,
    },
    isMobile ? [autoplayInstance] : []
  );

  const onPointerDown = useCallback(() => {
    autoplayInstance.stop();
  }, [autoplayInstance]);

  const onPointerUp = useCallback(() => {
    setTimeout(() => {
      autoplayInstance.reset();
      autoplayInstance.play();
    }, 2000);
  }, [autoplayInstance]);

  const exchanges = [
    {
      logo: "/logos/Binance.svg",
      buttonText: t('copyTrading.binance'),
      buttonLink: "https://www.binance.com/copy-trading/lead-details/4524810041090991617?ref=40189349",
    },
    {
      logo: "/logos/Bitget.png",
      buttonText: t('copyTrading.bitget'),
      buttonLink: "https://www.bitget.com/copy-trading/trader/bdb04f778fb63f5fa590/futures?clacCode=GFXWF3H3",
    }
  ];

  return (
    <section id="copytrading" className="section-spacing bg-bg-primary">
      <div className="container-custom">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
          {t('copyTrading.heading')}
        </h2>
        <p className="text-text-secondary text-center mb-4 max-w-2xl mx-auto">
          {t('copyTrading.description')}
        </p>
        <p className="text-text-secondary text-center mb-16 max-w-2xl mx-auto opacity-80">
          {t('copyTrading.leverage')}
        </p>
        
        <div 
          className={`overflow-hidden ${isMobile ? 'touch-pan-y' : ''}`} 
          ref={emblaRef}
          onPointerDown={isMobile ? onPointerDown : undefined}
          onPointerUp={isMobile ? onPointerUp : undefined}
          onTouchStart={isMobile ? onPointerDown : undefined}
          onTouchEnd={isMobile ? onPointerUp : undefined}
        >
          <div className="flex lg:justify-center">
            {exchanges.map((exchange, index) => (
              <ExchangeCard
                key={index}
                logo={exchange.logo}
                buttonText={exchange.buttonText}
                buttonLink={exchange.buttonLink}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CopyTrading;